/**
 * Progress Save/Load Helpers
 * Utilities for managing user progress tied to wallet addresses
 */

// Save progress for the given wallet address
export function saveProgress(walletAddress, progressData) {
  try {
    localStorage.setItem(
      `progress_${walletAddress}`,
      JSON.stringify(progressData)
    );
    return { success: true };
  } catch (err) {
    console.error('Failed to save progress:', err);
    return { success: false, error: err.message };
  }
}

// Load progress for the given wallet address
export function loadProgress(walletAddress) {
  try {
    const raw = localStorage.getItem(`progress_${walletAddress}`);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load progress:', err);
    return null;
  }
}

// Reset progress for the given wallet address
export function resetProgress(walletAddress) {
  try {
    localStorage.removeItem(`progress_${walletAddress}`);
    return { success: true };
  } catch (err) {
    console.error('Failed to reset progress:', err);
    return { success: false, error: err.message };
  }
}

// Validate progress data structure and types
export function validateProgressData(data) {
  // Check if data is an object
  if (typeof data !== 'object' || data === null) {
    throw new Error('Progress data must be an object');
  }

  // Validate required top-level structure
  const requiredKeys = ['tutorials', 'games', 'achievements', 'stats'];
  for (const key of requiredKeys) {
    if (!(key in data)) {
      throw new Error(`Missing required field: ${key}`);
    }
    if (typeof data[key] !== 'object' || data[key] === null) {
      throw new Error(`Invalid type for field: ${key}`);
    }
  }

  // Validate stats structure
  const stats = data.stats;
  if (typeof stats.totalTutorialsCompleted !== 'number' ||
      typeof stats.totalCodeExecutions !== 'number' ||
      typeof stats.totalGamesPlayed !== 'number' ||
      typeof stats.streakDays !== 'number') {
    throw new Error('Invalid stats structure');
  }

  // Validate value ranges (prevent absurd values)
  if (stats.totalTutorialsCompleted < 0 || stats.totalTutorialsCompleted > 1000 ||
      stats.totalCodeExecutions < 0 || stats.totalCodeExecutions > 100000 ||
      stats.totalGamesPlayed < 0 || stats.totalGamesPlayed > 10000 ||
      stats.streakDays < 0 || stats.streakDays > 10000) {
    throw new Error('Progress values out of bounds');
  }

  // Validate games structure
  for (const gameId in data.games) {
    const game = data.games[gameId];
    if (typeof game.level !== 'number' || typeof game.highScore !== 'number') {
      throw new Error(`Invalid game data for: ${gameId}`);
    }
    if (game.level < 1 || game.level > 100 || game.highScore < 0) {
      throw new Error(`Game values out of bounds for: ${gameId}`);
    }
  }

  return true;
}

// Export progress as base64 (for backup/share)
export function exportProgressBase64(walletAddress) {
  try {
    const data = localStorage.getItem(`progress_${walletAddress}`);
    if (!data) return '';
    
    // Validate data before exporting to ensure it's clean
    const parsed = JSON.parse(data);
    validateProgressData(parsed);
    
    return btoa(data); // encode as base64 for safe copy-pasting
  } catch (err) {
    console.error('Failed to export progress:', err);
    return '';
  }
}

// Import progress from base64 (for restore) - WITH STRICT VALIDATION
export function importProgressBase64(walletAddress, encodedData) {
  try {
    // Validate input is a string
    if (typeof encodedData !== 'string' || !encodedData.trim()) {
      throw new Error('Invalid input: expected non-empty string');
    }

    // Decode base64
    const raw = atob(encodedData);
    
    // Parse JSON (will throw if invalid)
    const data = JSON.parse(raw);
    
    // STRICT VALIDATION - throws if data is malformed or malicious
    validateProgressData(data);
    
    // Only save if all validation passes
    localStorage.setItem(`progress_${walletAddress}`, raw);
    
    return { success: true, message: 'Progress restored successfully!' };
  } catch (err) {
    console.error('Import validation failed:', err);
    return { 
      success: false, 
      error: err.message || 'Invalid or unsafe backup data. Only import files from this app.'
    };
  }
}

// Get all wallet addresses with saved progress
export function getAllProgressKeys() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('progress_')) {
        keys.push(key);
      }
    }
    return keys;
  } catch (err) {
    console.error('Failed to get progress keys:', err);
    return [];
  }
}

// Check if progress exists for wallet address
export function hasProgress(walletAddress) {
  try {
    return localStorage.getItem(`progress_${walletAddress}`) !== null;
  } catch (err) {
    return false;
  }
}

// Get progress size in bytes
export function getProgressSize(walletAddress) {
  try {
    const data = localStorage.getItem(`progress_${walletAddress}`);
    return data ? new Blob([data]).size : 0;
  } catch (err) {
    return 0;
  }
}

// ==================== CHECK-IN UTILITIES ====================

// Mark wallet check-in locally
export function setCheckIn(walletAddress) {
  try {
    const today = new Date().toISOString().split('T')[0];
    const existingCheckIn = getCheckIn(walletAddress);
    
    const checkInObj = {
      checkedIn: true,
      lastCheckInDate: today,
      todayDate: today,
      streakDays: calculateStreak(existingCheckIn, today),
      totalCheckIns: (existingCheckIn?.totalCheckIns || 0) + 1,
      checkInHistory: [
        ...(existingCheckIn?.checkInHistory || []).slice(-30), // Keep last 30 days
        today
      ]
    };
    
    localStorage.setItem(
      `checkin_${walletAddress}`,
      JSON.stringify(checkInObj)
    );
    
    return checkInObj;
  } catch (err) {
    console.error('Failed to check-in:', err);
    return null;
  }
}

// Check if wallet has checked in today
export function getCheckIn(walletAddress) {
  try {
    const raw = localStorage.getItem(`checkin_${walletAddress}`);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load check-in:', err);
    return null;
  }
}

// Check if user has checked in today
export function hasCheckedInToday(walletAddress) {
  try {
    const checkIn = getCheckIn(walletAddress);
    if (!checkIn) return false;
    
    const today = new Date().toISOString().split('T')[0];
    return checkIn.lastCheckInDate === today;
  } catch (err) {
    return false;
  }
}

// Calculate streak days
function calculateStreak(existingCheckIn, today) {
  if (!existingCheckIn || !existingCheckIn.lastCheckInDate) {
    return 1; // First check-in
  }
  
  const lastDate = new Date(existingCheckIn.lastCheckInDate);
  const todayDate = new Date(today);
  const diffTime = todayDate - lastDate;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    // Same day, keep streak
    return existingCheckIn.streakDays || 1;
  } else if (diffDays === 1) {
    // Next day, increment streak
    return (existingCheckIn.streakDays || 1) + 1;
  } else {
    // Streak broken, reset to 1
    return 1;
  }
}

// Get check-in stats
export function getCheckInStats(walletAddress) {
  const checkIn = getCheckIn(walletAddress);
  if (!checkIn) {
    return {
      streakDays: 0,
      totalCheckIns: 0,
      lastCheckInDate: null,
      hasCheckedInToday: false
    };
  }
  
  const today = new Date().toISOString().split('T')[0];
  return {
    streakDays: checkIn.streakDays || 0,
    totalCheckIns: checkIn.totalCheckIns || 0,
    lastCheckInDate: checkIn.lastCheckInDate,
    hasCheckedInToday: checkIn.lastCheckInDate === today,
    checkInHistory: checkIn.checkInHistory || []
  };
}

// Reset check-in data
export function resetCheckIn(walletAddress) {
  try {
    localStorage.removeItem(`checkin_${walletAddress}`);
    return { success: true };
  } catch (err) {
    console.error('Failed to reset check-in:', err);
    return { success: false, error: err.message };
  }
}

